import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { can } from '../../api/profile';
import {
  getPublicContactSettings,
  updateContactSettings,
  type ContactSettings,
  type EditableContactSettings,
  FALLBACK_CONTACT_SETTINGS,
} from '../../features/contactSettings';
import { ApiError } from '../../shared/api';
import { Card, DashboardPageHeader } from '../../shared/ui';
import type { SocialLink } from '../../shared/types';
import { parseSocialLinksJson, socialPlatformMeta } from '../../shared/lib';

const ContactDetailsPage: React.FC = () => {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rolePhones, setRolePhones] = useState<
    Pick<
      ContactSettings,
      | 'chairmanName'
      | 'chairmanPhone'
      | 'treasurerName'
      | 'treasurerPhone'
      | 'secretaryName'
      | 'secretaryPhone'
    >
  >({
    chairmanName: null,
    chairmanPhone: null,
    treasurerName: null,
    treasurerPhone: null,
    secretaryName: null,
    secretaryPhone: null,
  });
  const [form, setForm] = useState<EditableContactSettings>({
    contactNumber: '',
    whatsappNumber: '',
    address: '',
    emailAddress: '',
    accountName: '',
    accountNumber: '',
    bankName: '',
    branchName: '',
    branchCode: '',
    reference: '',
    footerLinksJson: null,
    donorTitle: '',
    donorDescriptionHtml: '',
  });
  const [footerLinks, setFooterLinks] = useState<SocialLink[]>([]);

  const canView = !!profile && can(profile, 'contactSettings', 'view');
  const canEdit = !!profile && can(profile, 'contactSettings', 'edit');

  useEffect(() => {
    if (!canView) return;
    void load();
  }, [canView]);

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const settings = await getPublicContactSettings();
      setForm({
        contactNumber: settings.contactNumber,
        whatsappNumber: settings.whatsappNumber,
        address: settings.address,
        emailAddress: settings.emailAddress,
        accountName: settings.accountName,
        accountNumber: settings.accountNumber,
        bankName: settings.bankName,
        branchName: settings.branchName,
        branchCode: settings.branchCode,
        reference: settings.reference ?? '',
        footerLinksJson: settings.footerLinksJson ?? null,
        donorTitle: settings.donorTitle,
        donorDescriptionHtml: settings.donorDescriptionHtml,
      });
      setFooterLinks(parseSocialLinksJson(settings.footerLinksJson));
      setRolePhones({
        chairmanName: settings.chairmanName,
        chairmanPhone: settings.chairmanPhone,
        treasurerName: settings.treasurerName,
        treasurerPhone: settings.treasurerPhone,
        secretaryName: settings.secretaryName,
        secretaryPhone: settings.secretaryPhone,
      });
    } catch (err) {
      console.error(err);
      setForm({
        contactNumber: FALLBACK_CONTACT_SETTINGS.contactNumber,
        whatsappNumber: FALLBACK_CONTACT_SETTINGS.whatsappNumber,
        address: FALLBACK_CONTACT_SETTINGS.address,
        emailAddress: FALLBACK_CONTACT_SETTINGS.emailAddress,
        accountName: FALLBACK_CONTACT_SETTINGS.accountName,
        accountNumber: FALLBACK_CONTACT_SETTINGS.accountNumber,
        bankName: FALLBACK_CONTACT_SETTINGS.bankName,
        branchName: FALLBACK_CONTACT_SETTINGS.branchName,
        branchCode: FALLBACK_CONTACT_SETTINGS.branchCode,
        reference: FALLBACK_CONTACT_SETTINGS.reference ?? '',
        footerLinksJson: FALLBACK_CONTACT_SETTINGS.footerLinksJson ?? null,
        donorTitle: FALLBACK_CONTACT_SETTINGS.donorTitle,
        donorDescriptionHtml: FALLBACK_CONTACT_SETTINGS.donorDescriptionHtml,
      });
      setFooterLinks(parseSocialLinksJson(FALLBACK_CONTACT_SETTINGS.footerLinksJson));
      setRolePhones({
        chairmanName: FALLBACK_CONTACT_SETTINGS.chairmanName,
        chairmanPhone: FALLBACK_CONTACT_SETTINGS.chairmanPhone,
        treasurerName: FALLBACK_CONTACT_SETTINGS.treasurerName,
        treasurerPhone: FALLBACK_CONTACT_SETTINGS.treasurerPhone,
        secretaryName: FALLBACK_CONTACT_SETTINGS.secretaryName,
        secretaryPhone: FALLBACK_CONTACT_SETTINGS.secretaryPhone,
      });
      setError('Failed to load settings. Showing fallback defaults.');
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (key: keyof EditableContactSettings, value: string) => {
    setForm((prev) => ({
      ...prev,
      [key]: key === 'reference' ? value : value,
    }));
  };

  const onSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canEdit) return;
    setError(null);
    try {
      setIsSaving(true);
      const sanitizedFooterLinks = footerLinks
        .map((link) => ({
          platform: (link.platform || 'custom').trim() || 'custom',
          url: (link.url || '').trim(),
          label: link.label?.trim() || undefined,
        }))
        .filter((link) => link.url.length > 0);
      const updated = await updateContactSettings({
        ...form,
        reference: form.reference?.trim() ? form.reference.trim() : null,
        footerLinksJson: sanitizedFooterLinks.length
          ? JSON.stringify(sanitizedFooterLinks)
          : null,
      });
      setFooterLinks(parseSocialLinksJson(updated.footerLinksJson));
      setRolePhones({
        chairmanName: updated.chairmanName,
        chairmanPhone: updated.chairmanPhone,
        treasurerName: updated.treasurerName,
        treasurerPhone: updated.treasurerPhone,
        secretaryName: updated.secretaryName,
        secretaryPhone: updated.secretaryPhone,
      });
      showToast('Contact, banking, and footer links updated successfully.');
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError && err.status === 400) {
        setError('Some details are invalid. Please check and try again.');
      } else if (err instanceof ApiError && err.status === 401) {
        setError('Your session has expired. Please sign in again.');
      } else {
        setError('Failed to save contact settings. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!canView) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-warning" role="alert">
            <i className="bi bi-shield-exclamation me-2" />
            <strong>Access Restricted:</strong> You do not have permission to view contact and donor settings.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <DashboardPageHeader
          title="Contact Details"
          description="Public contact numbers, banking details, footer links, and related settings."
          icon="bi bi-person-lines-fill"
        />

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </div>
        )}
        {canView && !canEdit && (
          <div className="alert alert-info mb-3" role="alert">
            <i className="bi bi-info-circle me-2" />
            You can view this section but your role does not allow editing. Ask an administrator to grant
            Contact settings — Edit if you need to make changes.
          </div>
        )}

        <Card title="Contact and Banking Settings" icon="bi bi-gear">
          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted mb-0">Loading settings...</p>
            </div>
          ) : (
            <form onSubmit={onSave}>
              <div className="row g-4">
                <div className="col-lg-6">
                  <h6 className="mb-3">Mosque Contact Information</h6>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Contact Number</label>
                      <input
                        className="form-control"
                        value={form.contactNumber}
                        onChange={(e) => onChange('contactNumber', e.target.value)}
                        required
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">WhatsApp Number</label>
                      <input
                        className="form-control"
                        value={form.whatsappNumber}
                        onChange={(e) => onChange('whatsappNumber', e.target.value)}
                        required
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Address</label>
                      <input
                        className="form-control"
                        value={form.address}
                        onChange={(e) => onChange('address', e.target.value)}
                        required
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        className="form-control"
                        value={form.emailAddress}
                        onChange={(e) => onChange('emailAddress', e.target.value)}
                        required
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label d-block mb-2">Footer Social/Web Links</label>
                      <div className="border rounded p-3 bg-light">
                        {footerLinks.length === 0 && (
                          <p className="text-muted small mb-2">
                            No footer links added yet.
                          </p>
                        )}
                        {footerLinks.map((link, idx) => {
                          const options = Object.entries(socialPlatformMeta).map(([value, meta]) => ({
                            value,
                            title: meta.title,
                            icon: meta.icon,
                          }));
                          const selected = options.find((opt) => opt.value === link.platform) ?? options[0];
                          return (
                            <div key={idx} className="row g-2 align-items-center mb-2">
                              <div className="col-12 col-md-3">
                                <label className="form-label form-label-sm mb-1">Platform</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={selected.value}
                                  onChange={(e) =>
                                    setFooterLinks((prev) =>
                                      prev.map((item, itemIdx) =>
                                        itemIdx === idx ? { ...item, platform: e.target.value } : item
                                      )
                                    )
                                  }
                                  disabled={!canEdit}
                                >
                                  {options.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.title}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-12 col-md-5">
                                <label className="form-label form-label-sm mb-1">URL</label>
                                <input
                                  type="url"
                                  className="form-control form-control-sm"
                                  placeholder="https://..."
                                  value={link.url}
                                  onChange={(e) =>
                                    setFooterLinks((prev) =>
                                      prev.map((item, itemIdx) =>
                                        itemIdx === idx ? { ...item, url: e.target.value } : item
                                      )
                                    )
                                  }
                                  disabled={!canEdit}
                                />
                              </div>
                              <div className="col-10 col-md-3">
                                <label className="form-label form-label-sm mb-1">Label (Optional)</label>
                                <input
                                  className="form-control form-control-sm"
                                  placeholder="Display label"
                                  value={link.label ?? ''}
                                  onChange={(e) =>
                                    setFooterLinks((prev) =>
                                      prev.map((item, itemIdx) =>
                                        itemIdx === idx ? { ...item, label: e.target.value } : item
                                      )
                                    )
                                  }
                                  disabled={!canEdit}
                                />
                              </div>
                              <div className="col-2 col-md-1 d-flex align-items-end">
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm w-100"
                                  onClick={() =>
                                    setFooterLinks((prev) => prev.filter((_, itemIdx) => itemIdx !== idx))
                                  }
                                  aria-label="Remove footer link"
                                  disabled={!canEdit}
                                >
                                  <i className="bi bi-trash" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm mt-2"
                          onClick={() =>
                            setFooterLinks((prev) => [...prev, { platform: 'facebook', url: '', label: '' }])
                          }
                          disabled={!canEdit}
                        >
                          <i className="bi bi-plus me-1" />
                          Add Footer Link
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <h6 className="mb-3">Mosque Banking Information</h6>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Account Name</label>
                      <input
                        className="form-control"
                        value={form.accountName}
                        onChange={(e) => onChange('accountName', e.target.value)}
                        required
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Account Number</label>
                      <input
                        className="form-control"
                        value={form.accountNumber}
                        onChange={(e) => onChange('accountNumber', e.target.value)}
                        required
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Bank Name</label>
                      <input
                        className="form-control"
                        value={form.bankName}
                        onChange={(e) => onChange('bankName', e.target.value)}
                        required
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Branch Name</label>
                      <input
                        className="form-control"
                        value={form.branchName}
                        onChange={(e) => onChange('branchName', e.target.value)}
                        required
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Branch Code</label>
                      <input
                        className="form-control"
                        value={form.branchCode}
                        onChange={(e) => onChange('branchCode', e.target.value)}
                        required
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Reference (Optional)</label>
                      <input
                        className="form-control"
                        value={form.reference ?? ''}
                        onChange={(e) => onChange('reference', e.target.value)}
                        disabled={!canEdit}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <hr className="my-4" />
              <h6 className="mb-3">Role-based Contacts (read-only)</h6>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Chairman</label>
                  <input
                    className="form-control"
                    value={
                      rolePhones.chairmanPhone
                        ? rolePhones.chairmanName
                          ? `${rolePhones.chairmanName} (${rolePhones.chairmanPhone})`
                          : rolePhones.chairmanPhone
                        : 'Not assigned'
                    }
                    disabled
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Treasurer</label>
                  <input
                    className="form-control"
                    value={
                      rolePhones.treasurerPhone
                        ? rolePhones.treasurerName
                          ? `${rolePhones.treasurerName} (${rolePhones.treasurerPhone})`
                          : rolePhones.treasurerPhone
                        : 'Not assigned'
                    }
                    disabled
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Secretary</label>
                  <input
                    className="form-control"
                    value={
                      rolePhones.secretaryPhone
                        ? rolePhones.secretaryName
                          ? `${rolePhones.secretaryName} (${rolePhones.secretaryPhone})`
                          : rolePhones.secretaryPhone
                        : 'Not assigned'
                    }
                    disabled
                  />
                </div>
              </div>

              <div className="mt-4 d-flex justify-content-end">
                <button className="btn btn-primary" type="submit" disabled={isSaving || !canEdit}>
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ContactDetailsPage;
